import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Tabs,
  Tab,
  useMediaQuery,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EquipmentManager from "../components/EquipmentManager";
import Dashboard from "./Dashboard";
import CCOPage from "./CCOPage";
import { getEquipments } from "../api/equipment";

export default function DashboardPage() {
  function getInitialTab() {
    const params = new URLSearchParams(window.location.search);
    const tab = Number(params.get("tab"));
    return [0, 1, 2].includes(tab) ? tab : 0;
  }

  const [selectedMac, setSelectedMac] = useState("");
  const [ambienteNome, setAmbienteNome] = useState("");

  const [selectedMacOut, setSelectedMacOut] = useState("");
  const [ambienteNomeOut, setAmbienteNomeOut] = useState("");

  const [tab, setTab] = useState(getInitialTab());
  const [modoSelecao, setModoSelecao] = useState("in");
  const [equipments, setEquipments] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const url = new URL(window.location);
    url.searchParams.set("tab", String(tab));
    window.history.replaceState({}, "", url);
  }, [tab]);

  useEffect(() => {
    getEquipments()
      .then((list) => setEquipments(list || []))
      .catch(() => setEquipments([]));
  }, [tab]);

  const limparInterno = () => {
    setSelectedMac("");
    setAmbienteNome("");
  };

  const limparExterno = () => {
    setSelectedMacOut("");
    setAmbienteNomeOut("");
  };

  const limparTudo = () => {
    limparInterno();
    limparExterno();
  };

  const equipamentoProps = useMemo(() => {
    if (modoSelecao === "out") {
      return {
        selectedMac: selectedMacOut,
        setSelectedMac: (mac) => setSelectedMacOut(mac),
        setAmbienteNome: (nome) => setAmbienteNomeOut(nome),
      };
    }

    return {
      selectedMac,
      setSelectedMac: (mac) => setSelectedMac(mac),
      setAmbienteNome: (nome) => setAmbienteNome(nome),
    };
  }, [modoSelecao, selectedMac, selectedMacOut]);

  const { primaryMac, primaryNome, refMac, refNome } = useMemo(() => {
    if (selectedMac) {
      return {
        primaryMac: selectedMac,
        primaryNome: ambienteNome,
        refMac: selectedMacOut,
        refNome: ambienteNomeOut,
      };
    }
    if (selectedMacOut) {
      return {
        primaryMac: selectedMacOut,
        primaryNome: ambienteNomeOut,
        refMac: "",
        refNome: "",
      };
    }
    return { primaryMac: "", primaryNome: "", refMac: "", refNome: "" };
  }, [selectedMac, ambienteNome, selectedMacOut, ambienteNomeOut]);

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: { xs: "calc(100vh - 56px)", md: "calc(100vh - 64px)" },
        p: { xs: 1, md: 3 },
      }}
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3 }}
        variant={isMobile ? "scrollable" : "standard"}
        scrollButtons={isMobile ? "auto" : false}
      >
        <Tab label="Painel" sx={{ fontSize: { xs: 13, sm: 15 } }} />
        <Tab label="Ambientes" sx={{ fontSize: { xs: 13, sm: 15 } }} />
        <Tab label="CCO" sx={{ fontSize: { xs: 13, sm: 15 } }} />
      </Tabs>

      {tab === 0 && (
        <Dashboard
          selectedMac={primaryMac}
          ambienteNome={primaryNome}
          selectedMacOut={refMac}
          ambienteNomeOut={refNome}
        />
      )}

      {tab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Selecionar equipamento
            </Typography>

            <ToggleButtonGroup
              value={modoSelecao}
              exclusive
              onChange={(_, v) => v && setModoSelecao(v)}
              size={isMobile ? "small" : "medium"}
              sx={{ mb: 1 }}
            >
              <ToggleButton value="in">Selecionar como INTERNO</ToggleButton>
              <ToggleButton value="out">Selecionar como EXTERNO (referência)</ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              <Chip
                label={selectedMac ? `Interno: ${ambienteNome || selectedMac}` : "Interno: não selecionado"}
                color={selectedMac ? "success" : "default"}
                variant={selectedMac ? "filled" : "outlined"}
              />
              <Chip
                label={selectedMacOut ? `Externo: ${ambienteNomeOut || selectedMacOut}` : "Externo: não selecionado"}
                color={selectedMacOut ? "info" : "default"}
                variant={selectedMacOut ? "filled" : "outlined"}
              />
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
              <Button
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                onClick={limparInterno}
                disabled={!selectedMac}
              >
                Limpar Interno
              </Button>

              <Button
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                onClick={limparExterno}
                disabled={!selectedMacOut}
              >
                Limpar Externo
              </Button>

              <Button
                variant="contained"
                color="warning"
                size={isMobile ? "small" : "medium"}
                onClick={limparTudo}
                disabled={!selectedMac && !selectedMacOut}
              >
                Limpar Tudo
              </Button>

              <Button
                variant="contained"
                color="primary"
                size={isMobile ? "small" : "medium"}
                onClick={() => setTab(0)}
                disabled={!selectedMac && !selectedMacOut}
              >
                Ir para o Painel
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Se o cliente tiver apenas um equipamento, basta selecionar um. Para análise comparativa, selecione também um externo de referência.
            </Typography>
          </Paper>

          <EquipmentManager
            selectedMac={equipamentoProps.selectedMac}
            setSelectedMac={equipamentoProps.setSelectedMac}
            setAmbienteNome={equipamentoProps.setAmbienteNome}
            setTab={setTab}
            autoGoToDashboard={false}
          />
        </Box>
      )}

      {tab === 2 && (
        <CCOPage
          equipments={equipments}
          selectedMacOut={selectedMacOut}
          ambienteNomeOut={ambienteNomeOut}
          onAbrirPainel={(mac, nome) => {
            setSelectedMac(mac);
            setAmbienteNome(nome);
            setTab(0);
          }}
        />
      )}
    </Box>
  );
}
